import { useEffect, useMemo, useState } from "react";
import { AmountField } from "@/components/ui/AmountField";
import { amountToInput } from "@/utils/amountInput";
import type { Category } from "@/hooks/useCategories";
import { ModalShell } from "@/components/ui/ModalShell";
import { FormField, ModalActions, SelectInput } from "@/components/ui/primitives";
import { useTranslation } from "react-i18next";
import "../budget.css";

interface NewBudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  assignedCategoryIds: number[];
  initialData?: {
    id?: number;
    category_id: number;
    amount: number;
    description?: string | null;
  } | null;
  onSubmit: (data: { category_id: number; amount: number; description?: string | null }) => Promise<void>;
}

export const NewBudgetModal = ({ isOpen, onClose, onSubmit, categories, assignedCategoryIds, initialData }: NewBudgetModalProps) => {
  const { t } = useTranslation(["common", "budget"]);
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [amount, setAmount] = useState("");
  const [showAmountError, setShowAmountError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sortedCategories = useMemo(() => [...categories].sort((a, b) => a.name.localeCompare(b.name)), [categories]);
  const numericAmount = Number(amount || 0);
  const hasValidAmount = Number.isFinite(numericAmount) && numericAmount > 0;

  useEffect(() => {
    if (!isOpen) return;
    setCategoryId(initialData?.category_id ?? "");
    setAmount(amountToInput(initialData?.amount));
    setShowAmountError(false);
    setSubmitError(false);
  }, [isOpen, initialData?.amount, initialData?.category_id]);

  const handleSubmit = async () => {
    if (!categoryId || !hasValidAmount) {
      setShowAmountError(true);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await onSubmit({
        category_id: Number(categoryId),
        amount: numericAmount,
        // Catalog form has only category and monthly limit; retain existing API text on edit.
        ...(initialData ? { description: initialData.description } : {}),
      });
      onClose();
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEditMode = Boolean(initialData);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="budget-modal-title"
      title={isEditMode ? t("budget.editBudget") : t("budget.newBudget")}
      subtitle={t("budget.newBudgetSubtitle")}
      mobileSheet
      variant="creation"
      maxWidthClassName="!w-[min(100%,560px)] !max-w-[560px]"
      panelClassName="account-modal-panel budget-modal-panel !h-fit"
      disableClose={isSubmitting}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      footer={
        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={isEditMode ? t("common.actions.update") : t("budget.createBudget")}
          submittingLabel={t("common.states.saving")}
          onCancel={onClose}
          isSubmitting={isSubmitting}
          isSubmitDisabled={!categoryId || !hasValidAmount}
        />
      }
    >
      <div className="budget-form">
        <p className="app-domain-sidebar-label budget-form-section">{t("budget.principalSection")}</p>
        <FormField label={t("common.labels.category")} htmlFor="budget-category" required>
          <SelectInput
            id="budget-category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value ? Number(event.target.value) : "")}
            required
            autoFocus
            disabled={isSubmitting}
          >
            <option value="">{t("budget.selectCategory")}</option>
            {sortedCategories.map((category) => {
              const disabled = assignedCategoryIds.includes(category.id) && category.id !== initialData?.category_id;
              return <option key={category.id} value={category.id} disabled={disabled}>{category.name}{disabled ? t("budget.alreadyAssigned") : ""}</option>;
            })}
          </SelectInput>
        </FormField>
        <FormField label={t("budget.monthlyLimit")} htmlFor="budget-amount" required error={showAmountError && !hasValidAmount ? t("budget.amountMustBePositive") : undefined} errorId="budget-amount-error">
          <AmountField
            id="budget-amount"
            value={amount}
            onChange={(value) => {
              setAmount(value);
              setShowAmountError(false);
            }}
            centsLabel={t("budget.amountCents")}
            invalid={showAmountError && !hasValidAmount}
            describedBy={showAmountError && !hasValidAmount ? "budget-amount-error" : undefined}
            required
            disabled={isSubmitting}
          />
        </FormField>
        {submitError ? <p role="alert" className="text-sm text-[var(--ds-negative)]">{t("budget.saveError")}</p> : null}
      </div>
    </ModalShell>
  );
};
